import type { Collaborator } from "@/types/trip"
import { CurrentUser, FollowStatus, PublicUser, UserRef } from "@/types/user"

import { links } from "@/lib/routes"
import { assert } from "@/lib/utils/assertions"
import { present } from "@/lib/utils/presence"

export function collaboratorName(collaborator: Collaborator) {
  const name = [collaborator.first_name, collaborator.last_name]
    .map((part) => part?.trim())
    .filter(present)
    .join(" ")

  return name || undefined
}

export function collaboratorDisplayName(collaborator: Collaborator) {
  return collaboratorName(collaborator) ?? collaborator.user.handle
}

export function collaboratorShortName(collaborator: Collaborator) {
  return (
    collaborator.first_name?.trim() ||
    collaborator.last_name?.trim() ||
    userDisplayShortName(collaborator.user)
  )
}

export function userDisplayName(
  user: CurrentUser | PublicUser,
  includeHandle = false
): string {
  if (user.first_name || user.last_name) {
    const parts: string[] = []
    if (user.first_name) parts.push(user.first_name)
    if (user.last_name) parts.push(user.last_name)
    if (includeHandle && user.handle) parts.push(`(@${user.handle})`)
    return parts.join(" ")
  }
  return user.handle || ""
}

export function userDisplayShortName(user: CurrentUser | PublicUser) {
  return user.first_name || user.last_name || user.handle
}

/**
 * Where this user's profile lives. Someone who has not set a handle has no
 * profile page at all: /profile/by-ref resolves a ref by looking up its
 * handle, so it 404s for exactly those users. Only anonymous users lack a
 * handle (the backend generates one at registration), so send them to
 * settings. Only CurrentUser can lack a handle, so this never fires for
 * someone else's profile.
 */
export function profilePath(user: Pick<PublicUser | CurrentUser, "handle">) {
  return user.handle ? profilePathForHandle(user.handle) : links.settings()
}

export function profilePathForHandle(handle: string) {
  return links.profile(handle)
}

export function profilePathForUserRef(ref: UserRef) {
  return links.profileByRef(ref)
}

export function publicUserForCurrentUser(user: CurrentUser): PublicUser {
  assert(user.handle, "should not get public user for anonymous user")
  return {
    user_ref: user.user_ref,
    handle: user.handle,
    first_name: user.first_name,
    last_name: user.last_name,
    // avatar_url: user.avatar_url,   TODO: maybe add this to user
  }
}

const COLOR_PAIRS = [
  "bg-blue-4 text-blue-12",
  "bg-crimson-4 text-crimson-12",
  "bg-cyan-4 text-cyan-12",
  "bg-gold-4 text-gold-12",
  "bg-green-4 text-green-12",
  "bg-indigo-4 text-indigo-12",
  "bg-orange-4 text-orange-12",
  "bg-plum-4 text-plum-12",
  "bg-sky-4 text-sky-12",
  "bg-teal-4 text-teal-12",
  "bg-tomato-4 text-tomato-12",
  "bg-yellow-4 text-yellow-12",
]

const hashString = (str: string) => {
  let hash = 0
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i)
    hash = (hash << 5) - hash + char
    hash |= 0
  }
  return Math.abs(hash)
}

export function avatarColorPair(userIdentifier: string) {
  const colorIndex = hashString(userIdentifier) % COLOR_PAIRS.length
  return COLOR_PAIRS[colorIndex]
}

export function isFollowingStatus(status: FollowStatus) {
  return status === "accepted" || status === "automatic"
}

export function isSearchable(
  user: CurrentUser | PublicUser | undefined
): boolean {
  // undefined user.searchable is treated as not searchable (users are not searchable by default)
  return user?.searchable === true
}
